// Per-type scaffolding templates for `cloudgrid scaffold service` and the
// idempotent re-scan in `cloudgrid scaffold`. Per decision 015's entry-point
// matrix — `node` → `src/index.js`, `python` → `src/main.py`, `static` →
// `index.html` (at the service root), `nextjs` → minimal Next.js project root,
// `cron` → language-resolved like `node`/`python`.
//
// Backlog 303 — `static` writes `index.html` at the service folder root, not
// `public/index.html`. The plug validator (checkServiceContentAlignment) and
// the deployer (no-build static COPYs the service root into nginx) both treat
// the service root as canonical, so a freshly-scaffolded static app plugs and
// serves without a manual file move.
//
// Idempotency rule: each entry returns a list of `{ relPath, content }`
// objects. The caller writes them only when the target file does not exist;
// user edits are never overwritten.

export type ServiceType = 'node' | 'nextjs' | 'python' | 'static' | 'cron';

export interface ScaffoldFile {
  relPath: string;
  content: string;
}

const NODE_PACKAGE_JSON = JSON.stringify(
  {
    name: '__SERVICE_NAME__',
    version: '0.1.0',
    private: true,
    type: 'commonjs',
    main: 'src/index.js',
    scripts: { start: 'node src/index.js' },
  },
  null,
  2,
) + '\n';

const NODE_INDEX_JS = `const http = require('http');

const port = Number(process.env.PORT) || 3000;
const server = http.createServer((req, res) => {
  res.writeHead(200, { 'Content-Type': 'text/plain' });
  res.end('hello from node\\n');
});
server.listen(port, () => {
  console.log('listening on ' + port);
});
`;

const NEXTJS_PACKAGE_JSON = JSON.stringify(
  {
    name: '__SERVICE_NAME__',
    version: '0.1.0',
    private: true,
    scripts: {
      dev: 'next dev',
      build: 'next build',
      start: 'next start',
    },
    dependencies: {
      next: '^14.2.0',
      react: '^18.3.0',
      'react-dom': '^18.3.0',
    },
    // Backlog 291 — the scaffold emits `.tsx` files and a `tsconfig.json`
    // with `strict: true`, so `next build` runs a type check. Without these
    // dev deps Next.js aborts the build asking the user to install
    // typescript + @types manually. Shipping them means the scaffold builds
    // out of the box with no edits.
    devDependencies: {
      typescript: '^5.4.0',
      '@types/node': '^20.12.0',
      '@types/react': '^18.3.0',
      '@types/react-dom': '^18.3.0',
    },
  },
  null,
  2,
) + '\n';

const NEXTJS_CONFIG_MJS = `/** @type {import('next').NextConfig} */
const nextConfig = {
  output: 'standalone',
};
export default nextConfig;
`;

const NEXTJS_TSCONFIG = JSON.stringify(
  {
    compilerOptions: {
      target: 'es2020',
      lib: ['dom', 'dom.iterable', 'esnext'],
      allowJs: true,
      skipLibCheck: true,
      strict: true,
      noEmit: true,
      esModuleInterop: true,
      module: 'esnext',
      moduleResolution: 'bundler',
      resolveJsonModule: true,
      isolatedModules: true,
      jsx: 'preserve',
      plugins: [{ name: 'next' }],
    },
    include: ['next-env.d.ts', '**/*.ts', '**/*.tsx'],
    exclude: ['node_modules'],
  },
  null,
  2,
) + '\n';

// Backlog 291 — `children` is typed so the `.tsx` scaffold passes the
// type check `next build` runs under `tsconfig.json`'s `strict: true`.
// `React.ReactNode` resolves from @types/react's global namespace, so no
// import is needed.
const NEXTJS_LAYOUT = `export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="en">
      <body>{children}</body>
    </html>
  );
}
`;

const NEXTJS_PAGE = `export default function Page() {
  return <main>hello from next.js</main>;
}
`;

const PYTHON_REQUIREMENTS = '';

const PYTHON_MAIN = `import http.server
import os
import socketserver

port = int(os.environ.get('PORT', '8080'))


class Handler(http.server.BaseHTTPRequestHandler):
    def do_GET(self):
        self.send_response(200)
        self.send_header('Content-Type', 'text/plain')
        self.end_headers()
        self.wfile.write(b'hello from python\\n')


with socketserver.TCPServer(('', port), Handler) as httpd:
    print(f'listening on {port}')
    httpd.serve_forever()
`;

const STATIC_INDEX_HTML = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>__SERVICE_NAME__</title>
  </head>
  <body>
    <h1>__SERVICE_NAME__</h1>
    <p>Replace this with your static content.</p>
  </body>
</html>
`;

const CRON_NODE_JOB = `console.log('cron job ran at', new Date().toISOString());
`;

const CRON_PYTHON_MAIN = `from datetime import datetime
print('cron job ran at', datetime.utcnow().isoformat() + 'Z')
`;

// Per decision 018: every service directory gets a `.env.example` at scaffold
// time. The dev workflow is: copy to `.env.local` (gitignored), fill in real
// values, the per-type runner / framework loads it natively. `cloudgrid plug`
// validates that every key listed here is set in `cloudgrid env list` OR
// `cloudgrid secrets list` for the entity before deploy.
const NODE_ENV_EXAMPLE = `# PORT is auto-injected by \`cloudgrid dev\`. Don't set it here.
# Reserved-resource URLs (MONGODB_URL, REDIS_URL, etc.) are also auto-injected.
#
# Add your app's env vars below. For local dev, copy this file to
# \`.env.local\` and fill in real values. The \`.env.local\` file is
# gitignored.
#
# Examples:
# LOG_LEVEL=info
# STRIPE_PUBLISHABLE_KEY=pk_test_...
`;

const NEXTJS_ENV_EXAMPLE = `# Next.js loads \`.env.local\` automatically. Public vars must start with NEXT_PUBLIC_.
#
# NEXT_PUBLIC_API_URL=http://localhost:3001
# NEXT_PUBLIC_STRIPE_KEY=pk_test_...
#
# Server-only secrets (don't add NEXT_PUBLIC_ prefix):
# STRIPE_SECRET_KEY=sk_test_...
`;

const PYTHON_ENV_EXAMPLE = `# python-dotenv loads \`.env.local\` if you \`from dotenv import load_dotenv; load_dotenv('.env.local')\`.
#
# LOG_LEVEL=info
`;

const STATIC_ENV_EXAMPLE = `# Most static apps don't need runtime env vars (they're build-time-baked).
# If yours does, document the build pipeline that injects them.
`;

function withName(content: string, serviceName: string): string {
  return content.replace(/__SERVICE_NAME__/g, serviceName);
}

/**
 * Files for a fresh service of the given type. Caller writes each only
 * when the target does not already exist.
 *
 * For `cron` the language-resolved files include both `package.json` (so
 * the build pipeline picks the node template) and `src/job.js`. Users
 * who prefer python can pass `--lang python` (future), but v0.8 ships
 * with node-default cron — matches the v0.7 default. `cloudgrid plug`
 * resolves cron language from on-disk files (decision 015 §"Cron language
 * resolution") so the choice is reversible by adding `requirements.txt`
 * and removing `package.json`.
 */
export function templateFor(type: ServiceType, serviceName: string): ScaffoldFile[] {
  switch (type) {
    case 'node':
      return [
        { relPath: 'package.json', content: withName(NODE_PACKAGE_JSON, serviceName) },
        { relPath: 'src/index.js', content: NODE_INDEX_JS },
        { relPath: '.env.example', content: NODE_ENV_EXAMPLE },
      ];
    case 'nextjs':
      return [
        { relPath: 'package.json', content: withName(NEXTJS_PACKAGE_JSON, serviceName) },
        { relPath: 'next.config.mjs', content: NEXTJS_CONFIG_MJS },
        { relPath: 'tsconfig.json', content: NEXTJS_TSCONFIG },
        { relPath: 'app/layout.tsx', content: NEXTJS_LAYOUT },
        { relPath: 'app/page.tsx', content: NEXTJS_PAGE },
        { relPath: '.env.example', content: NEXTJS_ENV_EXAMPLE },
      ];
    case 'python':
      return [
        { relPath: 'requirements.txt', content: PYTHON_REQUIREMENTS },
        { relPath: 'src/main.py', content: PYTHON_MAIN },
        { relPath: '.env.example', content: PYTHON_ENV_EXAMPLE },
      ];
    case 'static':
      return [
        { relPath: 'index.html', content: withName(STATIC_INDEX_HTML, serviceName) },
        { relPath: '.env.example', content: STATIC_ENV_EXAMPLE },
      ];
    case 'cron':
      return [
        { relPath: 'package.json', content: withName(JSON.stringify({ name: serviceName, version: '0.1.0', private: true, main: 'src/job.js' }, null, 2) + '\n', serviceName) },
        { relPath: 'src/job.js', content: CRON_NODE_JOB },
        { relPath: '.env.example', content: NODE_ENV_EXAMPLE },
      ];
    default: {
      const _exhaustive: never = type;
      void _exhaustive;
      throw new Error(`Unknown service type: ${type}`);
    }
  }
}

/** Helper for python-cron variant — exposed for future flag (`--lang python`). */
export function pythonCronTemplate(serviceName: string): ScaffoldFile[] {
  void serviceName;
  return [
    { relPath: 'requirements.txt', content: PYTHON_REQUIREMENTS },
    { relPath: 'src/job.py', content: CRON_PYTHON_MAIN },
    { relPath: '.env.example', content: PYTHON_ENV_EXAMPLE },
  ];
}
